import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiX, FiEye, FiEyeOff } from 'react-icons/fi';
import { createPortal } from 'react-dom';
import { supabase } from '../config/supabase';
import { useAuthContext } from '../hooks/useAuthContext';
import { sendPasswordRecoveryLink } from '../services/emailService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 18px 14px;
  box-sizing: border-box;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  padding: 28px;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 22px;
    border-radius: 14px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 24px;
  transition: all 0.2s;

  &:hover {
    color: #1f2937;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  outline: none;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  padding-right: 45px;
  width: 100%;
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }

  &:focus {
    outline: none;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InlineActionButton = styled.button`
  background: none;
  border: none;
  color: #5efba9;
  text-decoration: underline;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  padding: 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  color: #065f46;
  font-size: 14px;
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: -8px;
  margin-bottom: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #5efba9;
`;


const LinkText = styled(Link)`
  color: #5efba9;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

export default function LoginModal({ onClose }) {
  const { session } = useAuthContext();
  const portalTarget = useMemo(() => {
    if (typeof document === 'undefined') return null;
    return document.body;
  }, []);
  const [email, setEmail] = useState(() => {
    // Load saved email on component mount
    return localStorage.getItem('rememberedEmail') || '';
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    // Check if there's a saved email
    return !!localStorage.getItem('rememberedEmail');
  });
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const getResetRedirectUrl = () => {
    if (process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL) {
      return process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL;
    }

    const { protocol, hostname, port, origin } = window.location;
    const isPrivateNetworkHost =
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);

    if (isPrivateNetworkHost) {
      const safePort = port || '3000';
      return `${protocol}//localhost:${safePort}/reset-password`;
    }

    return `${origin}/reset-password`;
  };

  // Close modal if user becomes logged in
  React.useEffect(() => {
    if (session) {
      onClose();
    }
  }, [session, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message);
      } else {
        // Save or remove email based on "Remember Me" checkbox
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        // Send welcome email (fire and forget)
        try {
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://lxprwdplyftigaemzaua.supabase.co';
          const functionUrl = `${supabaseUrl}/functions/v1/send-welcome-email`;
          
          // Get user data
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            fetch(functionUrl, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imx4cHJ3ZHBseWZ0aWdhZW16YXVhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjE1NDEwMjUsImV4cCI6MjA3NzExNzAyNX0.-n4yL0Nsm5sgNiu8SqwyAVZQ3x2CyT_tEiYxeRJ_c8I',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId: user.id,
                email: user.email,
              }),
            }).catch(err => {
              console.error('Failed to send welcome email (non-blocking):', err);
            });
          }
        } catch (emailError) {
          console.error('Error triggering welcome email:', emailError);
          // Don't block login if email fails
        }
      }
      // Success - the useEffect will handle closing the modal
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setSuccessMessage('');

    if (!email) {
      setError('Please enter your email first.');
      return;
    }

    setForgotLoading(true);
    const redirectTo = getResetRedirectUrl();
    const { success, error: resetError } = await sendPasswordRecoveryLink({
      email,
      redirectTo,
    });
    setForgotLoading(false);

    if (!success) {
      setError(resetError || 'Failed to send password reset link.');
      return;
    }

    setSuccessMessage(`Password reset link sent to ${email}. Check inbox/spam.`);
  };

  const modal = (
    <ModalOverlay
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Login"
    >
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Login</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <Subtitle>Please log in to continue</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="modal-email">Email</Label>
            <Input
              id="modal-email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="modal-password">Password</Label>
            <PasswordInputWrapper>
              <PasswordInput
                id="modal-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <EyeIcon
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </EyeIcon>
            </PasswordInputWrapper>
            <RememberMeContainer>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                تذكرني - Remember Me
              </CheckboxLabel>
            </RememberMeContainer>
          </InputGroup>

          <Button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </Form>

        <LinkContainer>
          <InlineActionButton type="button" onClick={handleForgotPassword} disabled={forgotLoading}>
            {forgotLoading ? 'Sending reset link...' : 'Forgot password? Send reset link'}
          </InlineActionButton>
          <LinkText to="/signup">Don't have an account? Sign up</LinkText>
        </LinkContainer>
      </ModalContainer>
    </ModalOverlay>
  );

  // Render in a portal to avoid parent transforms/clipping (common in product cards / carousels).
  return portalTarget ? createPortal(modal, portalTarget) : modal;
}

